import React, { useState, useEffect, useRef } from 'react';
import { Alert, Modal, Button } from 'react-bootstrap';
import apiClient from '../lib/api-client';
import { fetchStateTopStory, getOfficesForState } from '../lib/nwsWeatherStories';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import '../CSS/TopNews.css';
import { useWindowWidth } from '../lib/useWindowWidth';

const TopNews = ({ selectedStateId = 'US' }) => {
  const [newsData, setNewsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobileViewport = useWindowWidth() < 768;

  // State weather story state
  const [stateStory, setStateStory] = useState(null);
  const [isStateLoading, setIsStateLoading] = useState(false);
  const [stateError, setStateError] = useState(null);

  // Session cache for state weather stories
  const storyCacheRef = useRef({});

  // Existing national headline fetch
  useEffect(() => {
    const fetchTopNews = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await apiClient.getTopNews();
        setNewsData(data);
      } catch (err) {
        console.error('Error fetching top news from endpoint /nws/top:', err);
        setError(`Failed to load news: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopNews();
  }, []);

  // State weather story fetch (tries multiple offices per state)
  useEffect(() => {
    if (selectedStateId === 'US') {
      setStateStory(null);
      setStateError(null);
      return;
    }

    const offices = getOfficesForState(selectedStateId);
    if (!offices) return; // No mapping — keep showing national

    // Cache hit
    if (storyCacheRef.current[selectedStateId]) {
      setStateStory(storyCacheRef.current[selectedStateId]);
      setStateError(null);
      return;
    }

    // Fetch from NWS (tries multiple offices)
    let cancelled = false;
    const doFetch = async () => {
      setIsStateLoading(true);
      setStateError(null);
      try {
        const topStory = await fetchStateTopStory(selectedStateId);
        if (cancelled) return;
        if (topStory) {
          storyCacheRef.current[selectedStateId] = topStory;
          setStateStory(topStory);
        } else {
          setStateError('No state weather stories available');
        }
      } catch (err) {
        if (!cancelled) setStateError('No state weather stories available');
      } finally {
        if (!cancelled) setIsStateLoading(false);
      }
    };

    doFetch();
    return () => { cancelled = true; };
  }, [selectedStateId]);

  // Determine display mode
  const isShowingState = selectedStateId !== 'US' && (stateStory || stateError || isStateLoading);

  // When showing state content, don't hide the component even if national data hasn't loaded
  if (!isShowingState) {
    if (isLoading) {
      return null; // Don't show anything while national is loading
    }
    if (error || !newsData) {
      return null; // Don't show banner if there's an error or no national data
    }
  }

  // Determine display content
  const displayTitle = isShowingState
    ? (stateStory?.title || stateError || 'Loading...')
    : (newsData?.title || 'Important Information');
  const displayDescription = isShowingState ? (stateStory?.description || null) : newsData?.description;

  // Format fetch_time if it exists
  const formatFetchTime = (fetchTime) => {
    if (!fetchTime) return null;
    try {
      const date = new Date(fetchTime);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return fetchTime;
    }
  };

  return (
    <>
      <Alert
        variant="info"
        className={`top-news-banner mb-3${isCollapsed ? ' top-news-collapsed' : ''}`}
      >
        {/* Toggle button always visible at top-right */}
        <button
          type="button"
          className="top-news-toggle"
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-label={isCollapsed ? 'Expand news banner' : 'Collapse news banner'}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? (
            <i className="fas fa-chevron-down" aria-hidden="true"></i>
          ) : (
            <i className="fas fa-chevron-up" aria-hidden="true"></i>
          )}
        </button>

        {isCollapsed ? (
          /* Collapsed state: only title */
          <div className="top-news-collapsed-content">
            <span className="top-news-collapsed-title">
              <i className="fas fa-info-circle me-2"></i>
              {displayTitle}
            </span>
          </div>
        ) : (
          /* Expanded state: full content */
          <div className="top-news-expanded">
            <Alert.Heading className="mb-2 top-news-title">
              <i className="fas fa-info-circle me-2"></i>
              {isStateLoading && isShowingState ? (
                <>
                  <output className="spinner-border spinner-border-sm me-2" aria-label="Loading state weather story"></output>
                  Loading state weather story...
                </>
              ) : (
                displayTitle
              )}
            </Alert.Heading>

            {/* Description area */}
            {!isStateLoading && displayDescription && (
              <p className="mb-1 top-news-description">
                {displayDescription}
                {/* Read more link only for national content */}
                {!isShowingState && !isMobileViewport && newsData?.link && (
                  <>
                    {' '}
                    <a
                      href={newsData.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="alert-link top-news-read-more"
                    >
                      Read more <i className="fas fa-external-link-alt ms-1"></i>
                    </a>
                  </>
                )}
              </p>
            )}

            {/* Mobile read more link - only for national content */}
            {!isShowingState && isMobileViewport && newsData?.link && (
              <a
                href={newsData.link}
                target="_blank"
                rel="noopener noreferrer"
                className="alert-link top-news-read-more"
              >
                Read more <i className="fas fa-external-link-alt ms-1"></i>
              </a>
            )}

            {/* Read more when no description - only for national content */}
            {!isShowingState && !displayDescription && !isMobileViewport && newsData?.link && (
              <a
                href={newsData.link}
                target="_blank"
                rel="noopener noreferrer"
                className="alert-link top-news-read-more"
              >
                Read more <i className="fas fa-external-link-alt ms-1"></i>
              </a>
            )}

            <div className="d-flex justify-content-between mt-1 top-news-meta">
              <div className="top-news-action">
                <button
                  type="button"
                  onClick={() => setShowImageModal(true)}
                  className="btn btn-link alert-link p-0 text-decoration-underline top-news-image-link"
                  style={{ verticalAlign: 'baseline' }}
                >
                  Population Impacted by Watches, Warnings and Advisories{' '}
                  <i className="fas fa-image ms-1"></i>
                </button>
              </div>
              <div className="top-news-date-wrap">
                {!isShowingState && newsData?.fetch_time && (
                  <small className="text-muted top-news-date">
                    {formatFetchTime(newsData.fetch_time)}
                  </small>
                )}
              </div>
            </div>
          </div>
        )}
      </Alert>

      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Population Impacted by Watches, Warnings and Advisories
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <Zoom>
            <img
              src="https://www.weather.gov/images/crh/dhs/wwa_population.png"
              alt="Population Impacted by Watches, Warnings and Advisories Map"
              className="img-fluid rounded"
            />
          </Zoom>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TopNews;
